/**
 * ForecastOverview — Forecast-Cockpit (#1632).
 *
 * - Handlungsbedarf-Widget oben: offene Reviews (+ älteste in Tagen), überfällige
 *   Abgeber des Monats, offene Aktionszeiträume — Kacheln verlinken in den
 *   Review-Arbeitsbereich (/forecast-review).
 * - Monats-Tabelle (X/Y) wie bisher; Monats-Detail via ?month=YYYY-MM (URL-State).
 * - Monats-Detail: Kundenliste in 3 Gruppen — (a) Handeln, (b) Abgeber/mit Forecast
 *   mit Status-Pill, (c) Ohne Forecast (n) eingeklappt mit Suche (ohne Deadline-Rot
 *   und ohne Erinnern — das lebt im Review-Arbeitsbereich).
 * - Score über Batch-Endpoint /api/customer-forecasts/scores (kein N+1).
 * - Abgeber-Flag kommt von der API (#1631 forecast-abgeber), kein FE-Nachbau.
 *
 * @page /forecast-overview
 * @roles admin, manager
 * @api /api/forecasts/overview, /api/customer-forecasts/status-overview,
 *      /api/customer-forecasts/pending, /api/customer-forecasts/scores, /api/aktionszeitraeume
 * @i18n forecastOverview, forecastReview
 * @since v2.789.0 (Cockpit-Umbau)
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Package, Users, TrendingUp, Check, X, PenLine, Inbox, AlertTriangle, Zap, ChevronDown, ChevronRight, Search, ClipboardCheck } from 'lucide-react';
import { Card, KpiCard, PageHeader, Button } from '../components/ui';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useLanguage } from '../context/LanguageContext';
import SharedForecastTabs from '../components/SharedForecastTabs';
import { StatusBadge as CfStatusBadge, AbgeberPill, StatusLegend } from './ForecastReview';

const API = import.meta.env.VITE_API_URL || '';

function apiFetch(path, opts) {
  return fetch(`${API}${path}`, { credentials: 'include', ...opts })
    .then(async r => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      return data;
    });
}

function fmt(n, d = 0) {
  if (n == null) return '\u2013';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtCompact(n) {
  if (n == null) return '\u2013';
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1000000) return (v / 1000000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
  if (abs >= 10000) return (v / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'K';
  return fmt(v);
}

function DiffBadge({ pct, prorata, t }) {
  if (pct == null) return <span className="text-content-tertiary">{'\u2013'}</span>;
  const abs = Math.abs(pct);
  let cls;
  if (abs <= 5) cls = 'bg-[rgba(107,114,128,0.1)] text-content-secondary';
  else if (pct > 0) cls = 'bg-emerald-500/10 text-emerald-500';
  else cls = 'bg-red-500/10 text-red-500';
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap" title={prorata && t ? t('forecastOverview.prorataHint') : undefined}>
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${cls}`}>
        {pct > 0 ? '+' : ''}{fmt(pct, 1)}%
      </span>
      {prorata && t && <span className="text-[9px] uppercase tracking-wide text-content-tertiary">{t('forecastOverview.prorata')}</span>}
    </span>
  );
}

function KundenCountCell({ count, total, t }) {
  if (!total) return <span className="font-mono text-xs text-content-secondary">{fmt(count)}</span>;
  const allDone = count >= total;
  const missingCount = total - count;
  return (
    <span className="font-mono text-xs whitespace-nowrap" title={allDone ? t('forecastOverview.allSubmitted') : t('forecastOverview.missingCount', { count: missingCount })}>
      <span className="text-content-secondary">{count}</span>
      <span className="text-content-tertiary"> / {total}</span>
      {allDone && <span className="ml-1 text-emerald-500">&#10003;</span>}
      {!allDone && missingCount > 0 && <span className="ml-1 text-red-500 text-[10px]">({missingCount})</span>}
    </span>
  );
}

function SourceBadge({ source, t }) {
  if (!source) return <span className="text-content-tertiary">{'\u2013'}</span>;
  if (source === 'customer') {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-[#338edc]">{t('forecastOverview.sourceCustomer')}</span>;
  }
  if (source === 'system') {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[rgba(107,114,128,0.1)] text-content-tertiary">System</span>;
  }
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[rgba(107,114,128,0.1)] text-content-secondary">{t('forecastOverview.sourceInternal')}</span>;
}

function ScoreBadge({ score }) {
  if (score == null) return <span className="text-content-tertiary text-xs">{'\u2013'}</span>;
  const s = Number(score);
  let dotCls;
  if (s >= 85) dotCls = 'bg-emerald-500';
  else if (s >= 60) dotCls = 'bg-amber-500';
  else dotCls = 'bg-red-500';
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-content-secondary">
      <span className={`w-2 h-2 rounded-full ${dotCls}`} />
      {s}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HANDLUNGSBEDARF-WIDGET (#1632 B1)
   ═══════════════════════════════════════════════════════════════ */

function ActionTile({ icon: Icon, label, value, sub, accent, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card)] p-4 hover:bg-[var(--bg-hover)] transition-colors"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-xl font-bold text-[var(--text-primary)]">{value}</div>
      {sub && <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{sub}</div>}
    </button>
  );
}

function ActionNeededWidget({ t, navigate }) {
  const [pending, setPending] = useState([]);
  const [azCount, setAzCount] = useState(0);
  const [overdueAbgeber, setOverdueAbgeber] = useState(null);
  const [cockpitMonth, setCockpitMonth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch('/api/customer-forecasts/pending').catch(() => []),
      apiFetch('/api/aktionszeitraeume?status=eingereicht').catch(() => []),
      apiFetch('/api/customer-forecasts/status-overview').catch(() => null),
    ]).then(([p, az, st]) => {
      if (cancelled) return;
      setPending(p || []);
      setAzCount((az || []).length);
      if (st?.customers) {
        setOverdueAbgeber(st.customers.filter(c => c.is_abgeber && c.status === 'overdue').length);
        setCockpitMonth(st.month);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const oldestDays = pending.length > 0
    ? Math.max(...pending.map(p => p.submitted_at ? Math.floor((Date.now() - new Date(p.submitted_at).getTime()) / 86400000) : 0))
    : null;

  const monthLink = cockpitMonth
    ? `/forecast-review?year=${cockpitMonth.split('-')[0]}&month=${parseInt(cockpitMonth.split('-')[1])}`
    : '/forecast-review';

  return (
    <div className="mb-6">
      <div className="text-sm font-semibold text-[var(--text-primary)] mb-2">{t('forecastOverview.actionNeeded')}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ActionTile
          icon={Inbox}
          label={t('forecastOverview.openReviews')}
          value={pending.length}
          sub={oldestDays != null ? t('forecastOverview.oldestDays', { days: oldestDays }) : t('forecastOverview.nothingOpen')}
          accent="#338edc"
          onClick={() => navigate('/forecast-review')}
        />
        <ActionTile
          icon={AlertTriangle}
          label={t('forecastOverview.overdueAbgeber')}
          value={overdueAbgeber == null ? '\u2013' : overdueAbgeber}
          sub={cockpitMonth || ''}
          accent="#F59E0B"
          onClick={() => navigate(monthLink)}
        />
        <ActionTile
          icon={Zap}
          label={t('forecastOverview.openPromos')}
          value={azCount}
          sub={azCount > 0 ? t('forecastOverview.promoWaiting') : t('forecastOverview.nothingOpen')}
          accent="#8B5CF6"
          onClick={() => navigate('/forecast-review?seg=aktionszeitraeume')}
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MONATS-LISTE
   ═══════════════════════════════════════════════════════════════ */

function OverviewList({ forecasts, totalActiveKunden, onSelectMonth, t, lang }) {
  const now = new Date();
  const curMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const active = forecasts.find(f => f.month === curMonth);

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-section">
        <KpiCard label={t('forecastOverview.totalForecasts')} value={forecasts.length} icon={Calendar} color="bg-[#EBF4FD] text-[#338edc]" />
        <KpiCard
          label={t('forecastOverview.customersCurrentMonth')}
          value={active ? `${fmt(active.kunden_count)} / ${totalActiveKunden}` : '\u2013'}
          icon={Users}
          color="bg-[#ECFEFF] text-[#22D3EE]"
        />
        <KpiCard label={t('forecastOverview.sollCurrentMonth')} value={active ? fmtCompact(active.soll_pakete) : '\u2013'} icon={Package} color="bg-[#ECFDF5] text-[#10B981]" />
        <KpiCard
          label={active?.diff_prorata ? `${t('forecastOverview.diffCurrentMonth')} (${t('forecastOverview.prorata')})` : t('forecastOverview.diffCurrentMonth')}
          value={active?.diff_pct != null ? `${active.diff_pct > 0 ? '+' : ''}${fmt(active.diff_pct, 1)}%` : '\u2013'}
          trend={active?.ist_pakete != null ? `${fmtCompact(active.ist_pakete)} IST` : undefined}
          trendUp={active?.diff_pct != null ? active.diff_pct >= 0 : undefined}
          icon={TrendingUp}
          color="bg-[#FFFBEB] text-[#F59E0B]"
        />
      </div>
      <Card padding="lg">
        {forecasts.length === 0 ? (
          <div className="py-12 text-center text-content-secondary text-sm">{t('forecastOverview.noForecasts')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                  <th className="py-3 pl-4 text-left">{t('forecastOverview.month')}</th>
                  <th className="py-3 text-left">{t('forecastOverview.status')}</th>
                  <th className="py-3 text-right">{t('forecastOverview.customers')}</th>
                  <th className="py-3 text-right">{t('forecastOverview.days')}</th>
                  <th className="py-3 text-right">{t('forecastOverview.soll')}</th>
                  <th className="py-3 text-right">{t('forecastOverview.ist')}</th>
                  <th className="py-3 text-right">{t('forecastOverview.diff')}</th>
                  <th className="py-3 text-right pr-4">{t('forecastOverview.fte')}</th>
                </tr>
              </thead>
              <tbody>
                {forecasts.map(f => (
                  <tr key={f.month} onClick={() => onSelectMonth(f)} className="border-b border-line hover:bg-surface-hover transition-colors cursor-pointer">
                    <td className="py-3 pl-4 font-medium text-content whitespace-nowrap">{lang === 'de' ? f.month_label_de : f.month_label_en}</td>
                    <td className="py-3"><StatusBadge status={f.status} /></td>
                    <td className="py-3 text-right"><KundenCountCell count={f.kunden_count} total={totalActiveKunden} t={t} /></td>
                    <td className="py-3 text-right font-mono text-xs text-content-secondary">{fmt(f.tage_count)}</td>
                    <td className="py-3 text-right font-mono text-xs text-content-secondary whitespace-nowrap" title={fmt(f.soll_pakete)}>{fmtCompact(f.soll_pakete)}</td>
                    <td className="py-3 text-right font-mono text-xs text-content-secondary whitespace-nowrap">{f.ist_pakete != null ? fmtCompact(f.ist_pakete) : <span className="text-content-tertiary">{'\u2013'}</span>}</td>
                    <td className="py-3 text-right"><DiffBadge pct={f.diff_pct} prorata={f.diff_prorata} t={t} /></td>
                    <td className="py-3 text-right pr-4 font-mono text-xs text-content-secondary">{fmt(f.fte_soll, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

/* ─── Inline Entry Row ──────────────────────────────── */
function InlineEntryRow({ kunde, month, onSaved, onCancel, t }) {
  const [volume, setVolume] = useState('');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    const vol = parseInt(volume, 10);
    if (!vol || vol <= 0) return;
    setSaving(true);
    try {
      await apiFetch('/api/forecast/entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: kunde.kunde_id, year: month.jahr, month: month.monat, volume: vol }),
      });
      onSaved();
    } catch (err) { console.error('Inline entry error:', err); }
    finally { setSaving(false); }
  };
  return (
    <tr className="border-b border-line bg-blue-50/50 dark:bg-blue-950/20">
      <td className="py-2 pl-4 font-medium text-content">{kunde.kunde_name}</td>
      <td className="py-2">
        {kunde.segment ? <span className="text-xs font-semibold" style={{ color: { A: '#338edc', B: '#6366F1', C: '#F59E0B' }[kunde.segment] || 'var(--text-secondary)' }}>{kunde.segment}</span> : <span className="text-content-tertiary">{'\u2013'}</span>}
      </td>
      <td className="py-2" colSpan={2}>
        <div className="flex items-center gap-2">
          <input type="number" value={volume} onChange={e => setVolume(e.target.value)} placeholder={t('forecastOverview.inlineVolume')}
            className="w-28 px-2 py-1 text-xs border border-[var(--border-default,#E5E7EB)] rounded bg-surface text-content focus:border-[#338edc] focus:outline-none"
            autoFocus min="1" onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onCancel(); }} />
          <button onClick={handleSave} disabled={saving || !volume || parseInt(volume, 10) <= 0} className="p-1 rounded hover:bg-emerald-500/10 text-emerald-600 disabled:opacity-40 transition-colors" title={t('forecastOverview.inlineSave')}><Check className="w-4 h-4" /></button>
          <button onClick={onCancel} className="p-1 rounded hover:bg-red-500/10 text-red-500 transition-colors" title={t('forecastOverview.inlineCancel')}><X className="w-4 h-4" /></button>
        </div>
      </td>
      <td colSpan={5}></td>
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MONATS-DETAIL — 3 Gruppen (#1632 B1)
   ═══════════════════════════════════════════════════════════════ */

function GroupHeader({ label, count, accent }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-inset,#F8F9FC)] border-b border-line">
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent }} />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{label} ({count})</span>
    </div>
  );
}

function MonthDetail({ month, onBack, t, lang, navigate }) {
  const [kunden, setKunden] = useState([]);
  const [prorata, setProrata] = useState({ active: false, ratio: 1 });
  const [meta, setMeta] = useState({ with: 0, total: 0 });
  const [stMap, setStMap] = useState({});
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [showOhne, setShowOhne] = useState(false);
  const [search, setSearch] = useState('');

  const monthStr = `${month.jahr}-${String(month.monat).padStart(2, '0')}`;

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch(`/api/forecasts/overview/${month.jahr}/${month.monat}`),
      apiFetch(`/api/customer-forecasts/status-overview?month=${monthStr}`).catch(() => null),
      apiFetch(`/api/customer-forecasts/scores?year=${month.jahr}&month=${month.monat}`).catch(() => null),
    ])
      .then(([d, st, sc]) => {
        setKunden(d.kunden || []);
        setProrata({ active: !!d.is_prorata, ratio: d.prorata_ratio || 1 });
        setMeta({ with: d.kunden_with_forecast || 0, total: d.total_active_kunden || 0 });
        const m = {};
        for (const c of st?.customers || []) m[c.customer_id] = c;
        setStMap(m);
        setScores(sc?.scores || {});
      })
      .catch(() => { setKunden([]); setMeta({ with: 0, total: 0 }); setStMap({}); setScores({}); })
      .finally(() => setLoading(false));
  }, [month.jahr, month.monat, monthStr]);

  useEffect(() => { loadData(); }, [loadData]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
  const handleEntrySaved = () => { setEditingId(null); showToast(t('forecastOverview.entryCreated')); loadData(); };

  const withForecast = kunden.filter(k => k.has_forecast !== false);
  const totals = withForecast.reduce((acc, k) => ({ soll: acc.soll + (k.soll_pakete || 0), ist: acc.ist + (k.ist_pakete || 0), fte: acc.fte + (k.fte_total || 0) }), { soll: 0, ist: 0, fte: 0 });
  // #1633: im aktiven Monat SOLL anteilig (hochgerechnete Abweichung, konsistent zu den Zeilen)
  const totalsSollBasis = totals.soll * (prorata.active ? prorata.ratio : 1);
  const totalDiff = totalsSollBasis > 0 && totals.ist > 0 ? Math.round(((totals.ist - totalsSollBasis) / totalsSollBasis) * 1000) / 10 : null;
  const monthLabel = lang === 'de' ? month.month_label_de : month.month_label_en;
  const segColors = { A: '#338edc', B: '#6366F1', C: '#F59E0B' };

  // Effektiver Status: cf_status (customer_forecasts) hat Vorrang, sonst
  // status-overview (kennt overdue/missing), sonst interner Forecast-Status.
  const enriched = kunden.map(k => {
    const st = stMap[k.kunde_id];
    const effStatus = (k.cf_status && k.cf_status !== 'draft') ? k.cf_status : (st?.status || (k.has_forecast !== false ? 'approved' : 'missing'));
    return { ...k, eff_status: effStatus, cf_id: k.cf_id || st?.cf_id || null, is_abgeber: k.is_abgeber ?? st?.is_abgeber ?? false };
  });

  const groupA = enriched.filter(k => ['submitted', 'in_review'].includes(k.eff_status) || (k.is_abgeber && k.eff_status === 'overdue'));
  const groupB = enriched.filter(k => !groupA.includes(k) && (k.is_abgeber || k.has_forecast !== false));
  const groupC = enriched.filter(k => !groupA.includes(k) && !groupB.includes(k));
  const groupCFiltered = search
    ? groupC.filter(k => (k.kunde_name || '').toLowerCase().includes(search.toLowerCase()))
    : groupC;

  const renderRow = (k, { actionable = false } = {}) => {
    const isMissing = k.has_forecast === false;
    if (isMissing && editingId === k.kunde_id) {
      return <InlineEntryRow key={k.kunde_id} kunde={k} month={month} onSaved={handleEntrySaved} onCancel={() => setEditingId(null)} t={t} />;
    }
    const clickTarget = k.cf_id ? `/kunden-forecasts/${k.cf_id}` : (isMissing ? null : `/forecast?kunde_id=${k.kunde_id}&jahr=${month.jahr}&monat=${month.monat}`);
    const score = scores[k.kunde_id]?.overall_score;
    return (
      <tr key={k.kunde_id} onClick={clickTarget ? () => navigate(clickTarget) : undefined}
        className={`border-b border-line transition-colors ${clickTarget ? 'hover:bg-surface-hover cursor-pointer' : 'opacity-70'}`}>
        <td className="py-3 pl-4 font-medium text-content">
          <span className="flex items-center gap-2">
            {k.kunde_name}
            {k.is_abgeber && <AbgeberPill t={t} />}
          </span>
        </td>
        <td className="py-3">{k.segment ? <span className="text-xs font-semibold" style={{ color: segColors[k.segment] || 'var(--text-secondary)' }}>{k.segment}</span> : <span className="text-content-tertiary">{'\u2013'}</span>}</td>
        <td className="py-3"><CfStatusBadge status={k.eff_status} t={t} /></td>
        <td className="py-3"><SourceBadge source={k.source} t={t} /></td>
        <td className="py-3 text-right font-mono text-xs text-content-secondary whitespace-nowrap">{isMissing ? <span className="text-content-tertiary">{'\u2013'}</span> : <span title={fmt(k.soll_pakete)}>{fmtCompact(k.soll_pakete)}</span>}</td>
        <td className="py-3 text-right font-mono text-xs text-content-secondary whitespace-nowrap">{k.ist_pakete != null ? <span title={fmt(k.ist_pakete)}>{fmtCompact(k.ist_pakete)}</span> : <span className="text-content-tertiary">{'\u2013'}</span>}</td>
        <td className="py-3 text-right">{isMissing ? (
          <button onClick={e => { e.stopPropagation(); setEditingId(k.kunde_id); }} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-[#338edc] hover:bg-blue-500/10 transition-colors"><PenLine className="w-3 h-3" />{t('forecastOverview.enterForecast')}</button>
        ) : <DiffBadge pct={k.diff_pct} prorata={k.diff_prorata} t={t} />}</td>
        <td className="py-3 text-center"><ScoreBadge score={score} /></td>
        <td className="py-3 text-right pr-4">
          {actionable && k.cf_id && ['submitted', 'in_review'].includes(k.eff_status) ? (
            <button onClick={e => { e.stopPropagation(); navigate(`/kunden-forecasts/${k.cf_id}`); }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-[#338edc] hover:bg-blue-500/10 transition-colors">
              <ClipboardCheck className="w-3 h-3" />{t('forecastOverview.toReview')}
            </button>
          ) : (
            <span className="font-mono text-xs text-content-secondary">{isMissing ? '' : fmt(k.fte_total, 1)}</span>
          )}
        </td>
      </tr>
    );
  };

  const tableHead = (
    <thead>
      <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
        <th className="py-3 pl-4 text-left">{t('forecastOverview.customer')}</th>
        <th className="py-3 text-left">{t('forecastOverview.segment')}</th>
        <th className="py-3 text-left">{t('forecastOverview.status')}</th>
        <th className="py-3 text-left">{t('forecastOverview.source')}</th>
        <th className="py-3 text-right">{t('forecastOverview.soll')}</th>
        <th className="py-3 text-right">{t('forecastOverview.ist')}</th>
        <th className="py-3 text-right">{t('forecastOverview.diff')}</th>
        <th className="py-3 text-center">{t('forecast.cs.score')}</th>
        <th className="py-3 text-right pr-4">{t('forecastOverview.fte')}</th>
      </tr>
    </thead>
  );

  return (
    <>
      {toast && <div className="fixed top-4 right-4 z-50 bg-surface border border-line rounded-lg px-4 py-2 shadow-sm text-sm text-content">{toast}</div>}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors text-content-secondary"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h2 className="text-lg font-bold text-content">{t('forecastOverview.detailTitle', { month: monthLabel })}</h2>
          <p className="text-xs text-content-secondary mt-0.5 flex items-center gap-2">
            <StatusBadge status={month.status} />
            <span className="text-content-tertiary">{t('forecastOverview.customersCount', { count: meta.with, total: meta.total })}</span>
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-section">
        <KpiCard label={t('forecastOverview.customers')} value={`${meta.with} / ${meta.total}`} icon={Users} color="bg-[#EBF4FD] text-[#338edc]" />
        <KpiCard label={t('forecastOverview.soll')} value={fmtCompact(totals.soll)} icon={Package} color="bg-[#ECFEFF] text-[#22D3EE]" />
        <KpiCard label={t('forecastOverview.ist')} value={totals.ist > 0 ? fmtCompact(totals.ist) : '\u2013'} color="bg-[#ECFDF5] text-[#10B981]" />
        <KpiCard label={prorata.active ? `${t('forecastOverview.diff')} (${t('forecastOverview.prorata')})` : t('forecastOverview.diff')} value={totalDiff != null ? `${totalDiff > 0 ? '+' : ''}${fmt(totalDiff, 1)}%` : '\u2013'} trend={totals.ist > 0 ? `${fmtCompact(totals.ist)} / ${fmtCompact(totals.soll)}` : undefined} trendUp={totalDiff != null ? totalDiff >= 0 : undefined} color="bg-[#FFFBEB] text-[#F59E0B]" />
      </div>
      <Card padding="lg">
        {loading ? (
          <div className="py-12 text-center text-content-secondary text-sm">{t('common.loading')}</div>
        ) : kunden.length === 0 ? (
          <div className="py-12 text-center text-content-secondary text-sm">{t('forecastOverview.noForecasts')}</div>
        ) : (
          <div className="overflow-x-auto">
            {groupA.length > 0 && (
              <>
                <GroupHeader label={t('forecastOverview.groupHandeln')} count={groupA.length} accent="#F59E0B" />
                <table className="w-full text-sm mb-4">
                  {tableHead}
                  <tbody>{groupA.map(k => renderRow(k, { actionable: true }))}</tbody>
                </table>
              </>
            )}
            <GroupHeader label={t('forecastOverview.groupAbgeber')} count={groupB.length} accent="#338edc" />
            <table className="w-full text-sm">
              {tableHead}
              <tbody>
                {groupB.length === 0 ? (
                  <tr><td colSpan={9} className="py-6 text-center text-content-secondary text-sm">{t('forecastOverview.noForecasts')}</td></tr>
                ) : groupB.map(k => renderRow(k))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line font-bold">
                  <td className="py-3 pl-4 text-content" colSpan={4}>{t('common.total')}</td>
                  <td className="py-3 text-right font-mono text-xs text-content whitespace-nowrap">{fmtCompact(totals.soll)}</td>
                  <td className="py-3 text-right font-mono text-xs text-content whitespace-nowrap">{totals.ist > 0 ? fmtCompact(totals.ist) : '\u2013'}</td>
                  <td className="py-3 text-right"><DiffBadge pct={totalDiff} prorata={prorata.active} t={t} /></td>
                  <td className="py-3"></td>
                  <td className="py-3 text-right pr-4 font-mono text-xs text-content">{fmt(totals.fte, 1)}</td>
                </tr>
              </tfoot>
            </table>
            {groupC.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={() => setShowOhne(v => !v)}
                  className="flex items-center gap-2 px-4 py-2 w-full text-left bg-[var(--bg-inset,#F8F9FC)] border-b border-line hover:bg-[var(--bg-hover)] transition-colors"
                >
                  {showOhne ? <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)]" /> : <ChevronRight className="w-4 h-4 text-[var(--text-tertiary)]" />}
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                    {t('forecastOverview.groupOhne')} ({groupC.length})
                  </span>
                </button>
                {showOhne && (
                  <>
                    <div className="flex items-center gap-2 px-4 py-2 border-b border-line">
                      <Search className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
                      <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('forecastOverview.searchCustomer')}
                        className="flex-1 text-xs bg-transparent text-content focus:outline-none"
                      />
                    </div>
                    <table className="w-full text-sm">
                      {tableHead}
                      <tbody>
                        {groupCFiltered.length === 0 ? (
                          <tr><td colSpan={9} className="py-6 text-center text-content-secondary text-sm">{t('forecastOverview.noForecasts')}</td></tr>
                        ) : groupCFiltered.map(k => renderRow(k))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <div className="mt-4 pt-3 border-t border-line">
          <StatusLegend t={t} />
        </div>
      </Card>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE — Cockpit
   ═══════════════════════════════════════════════════════════════ */

export default function ForecastOverview() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [forecasts, setForecasts] = useState([]);
  const [totalActiveKunden, setTotalActiveKunden] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(() => {
    setLoading(true);
    apiFetch('/api/forecasts/overview')
      .then(d => { setForecasts(d.forecasts || []); setTotalActiveKunden(d.total_active_kunden || 0); })
      .catch(() => { setForecasts([]); setTotalActiveKunden(0); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // URL-State: ?month=YYYY-MM öffnet das Monats-Detail (übersteht Reload)
  const monthParam = searchParams.get('month');
  const selectedMonth = monthParam ? forecasts.find(f => f.month === monthParam) : null;

  const setMonthParam = (val) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (val) p.set('month', val); else p.delete('month');
      return p;
    }, { replace: true });
  };

  if (selectedMonth) {
    return (
      <div className="p-6">
        <SharedForecastTabs />
        <MonthDetail
          month={selectedMonth}
          onBack={() => { setMonthParam(null); loadOverview(); }}
          t={t}
          lang={language}
          navigate={navigate}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <SharedForecastTabs />
      <PageHeader
        title={t('forecastOverview.cockpitTitle')}
        subtitle={t('forecastOverview.cockpitSubtitle')}
        actions={
          <Button variant="primary" size="sm" onClick={() => navigate('/forecast?wizard=1')}>
            + {t('forecast.wizard.new_forecast')}
          </Button>
        }
      />

      <ActionNeededWidget t={t} navigate={navigate} />

      {loading ? (
        <Card padding="lg"><div className="py-12 text-center text-content-secondary text-sm">{t('common.loading')}</div></Card>
      ) : (
        <OverviewList
          forecasts={forecasts}
          totalActiveKunden={totalActiveKunden}
          onSelectMonth={(f) => setMonthParam(f.month)}
          t={t}
          lang={language}
        />
      )}
    </div>
  );
}
